import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StayConnected from '../../components/StayConnected/StayConnected';
import './LandingPage.css';

import desktopPhoto from '../../assets/images/phone-1.png';
import chatPanelImage from '../../assets/images/DesktopPhoto.png';
import phasePalSteamImage from '../../assets/images/phase-pal-steam.png';

const kineticSections = [
  {
    title: 'Phase Fitness',
    body: 'Your AI-powered fitness companion. Personalized workout plans, nutrition logging with photo analysis, and adaptive coaching that evolves with your goals.',
  },
  {
    title: 'Phase Pal',
    body: 'Your AI pal on every game night. Animated 3D companions that sit on your desktop, chat with you, and react while you play, grind, or stream.',
  },
  {
    title: 'Adaptive Intelligence',
    body: 'Both products learn from you: Phase Fitness tracks your progress and adjusts plans, while Phase Pal remembers your conversations and adapts to your gaming style.',
  },
];

const brutalistTiles = [
  {
    label: 'Phase Fitness',
    description: 'AI-powered workout plans, nutrition photo analysis, and adaptive coaching that evolves with your fitness journey.',
    tone: 'mustard',
  },
  {
    label: 'Phase Pal',
    description: '3D animated companions for gamers. Chat, react, and customize with VRM avatars, custom voices, and personalities.',
    tone: 'rust',
  },
  {
    label: 'Smart AI',
    description: 'Both products learn from you, tracking progress, remembering conversations, and adapting to your style.',
    tone: 'clay',
  },
  {
    label: 'Privacy First',
    description: 'Both products run locally with offline modes. Your data stays on your device; no cloud required for core features.',
    tone: 'sand',
  },
];

const ritualMoments = [
  'Phase Fitness: Log meals with photos, get instant macro analysis, and receive personalized workout plans.',
  'Phase Pal: Download on Steam, customize your avatar and voice, then chat while you game or stream.',
  'Both products work offline with local mode for privacy and performance.',
  'Built for creators: streamers and VTubers can use Phase Pal as co-hosts or lore characters.',
];

const LandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideshowImages = [desktopPhoto, chatPanelImage];

  useEffect(() => {
    // Feature detection for IntersectionObserver
    if (typeof window.IntersectionObserver === 'undefined') {
      // Fallback: add visible class immediately if IntersectionObserver is not supported
      const blocks = document.querySelectorAll<HTMLElement>('.brutal-block');
      blocks.forEach((block) => block.classList.add('is-visible'));
      return;
    }

    const blocks = document.querySelectorAll<HTMLElement>('.brutal-block');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    blocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let current = 0;
    let target = 0;
    let raf: number;

    const animate = () => {
      current += (target - current) * 0.08;
      document.documentElement.style.setProperty('--scroll-weight', `${current * 0.015}px`);
      document.documentElement.style.setProperty('--scroll-tilt', `${current * 0.002}deg`);
      raf = window.requestAnimationFrame(animate);
    };

    const handleScroll = () => {
      target = window.scrollY;
    };

    animate();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [slideshowImages.length]);

  return (
    <div className="landing-page">
      <section className="hero brutal-block">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Consistency AI / Phase Fitness & Phase Pal</p>
            <h1 className="hero-title impact">
              AI companions for fitness and gaming.
            </h1>
            <p className="hero-subtitle">
              Phase Fitness powers your workout journey with adaptive coaching and smart nutrition tracking. 
              Phase Pal brings animated 3D companions to your desktop for gamers and streamers. 
              Both products learn from you and adapt to your style, all while keeping your data private and local.
            </p>
            <div className="hero-actions">
              <a href="https://store.steampowered.com/app/3655450/Phase_Pal/" target="_blank" rel="noopener noreferrer" className="cta primary-cta">
                Get Started
              </a>
              <a href="https://phasepal.com" target="_blank" rel="noopener noreferrer" className="cta ghost-cta">
                Explore Products
              </a>
            </div>
            <div className="hero-tags">
              <span>Phase Fitness</span>
              <span>Phase Pal</span>
              <span>AI-Powered</span>
            </div>
          </div>

          <div className="hero-panel">
            <div className="panel-mask">
              <img src={slideshowImages[currentSlide]} alt="Phase app preview" className="phone" />
              <div className={`floating-chip ${currentSlide === 0 ? 'bright' : ''}`}>Phase Fitness</div>
              <div className={`floating-chip alt ${currentSlide === 1 ? 'bright' : ''}`}>Phase Pal</div>
            </div>
            <div className="hero-stat brutal-block">
              <div className="stat-number">2</div>
              <p className="stat-label">Powerful AI products. One mission: elevate your performance.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="steam-showcase brutal-block">
        <div className="steam-showcase-container">
          <img src={phasePalSteamImage} alt="Phase Pal on Steam" className="steam-image" />
          <div className="steam-text">
            <h2 className="impact">Phase Pal Now Available on Steam</h2>
            <p>
              Download your AI gaming companion today. Customize avatars, voices, and personalities. 
              Perfect for solo gamers, streamers, and VTubers. Works offline with local processing for privacy and performance.
            </p>
            <a href="https://store.steampowered.com/app/3655450/Phase_Pal/" target="_blank" rel="noopener noreferrer" className="cta primary-cta">
              Download on Steam
            </a>
          </div>
        </div>
      </section>

      <section className="kinetic-section brutal-block">
        <div className="section-header">
          <h2 className="impact">Two Products, One Vision</h2>
          <p>
            Phase Fitness transforms your workout routine with AI-powered coaching and nutrition tracking. 
            Phase Pal brings animated companions to gamers and streamers. Both products feature intelligent AI that adapts to you, 
            with powerful local processing that keeps your data secure and private.
          </p>
        </div>
        <div className="kinetic-grid">
          {kineticSections.map((item) => (
            <div key={item.title} className="kinetic-card">
              <div className="card-header">
                <span className="card-dot" />
                <h3 className="impact">{item.title}</h3>
              </div>
              <p>{item.body}</p>
              <div className="hover-line" aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>

      <section className="brutalist-lab brutal-block">
        <div className="lab-grid">
          {brutalistTiles.map((tile) => (
            <article key={tile.label} className={`lab-tile tone-${tile.tone}`}>
              <div className="tile-top">
                <span className="tile-label">{tile.label}</span>
                <span className="tile-icon" aria-hidden="true">
                  ↘
                </span>
              </div>
              <p>{tile.description}</p>
            </article>
          ))}
        </div>
        <div className="lab-footer">
          <div className="divider" aria-hidden="true" />
          <div className="lab-meta">
            <p>Built for performance and privacy. Phase Fitness adapts to your fitness goals while Phase Pal learns your gaming style, all running locally on your device.</p>
            <Link to="/team" className="cta underline-cta">
              Meet the builders
            </Link>
          </div>
        </div>
      </section>

      <section className="rituals brutal-block">
        <div className="rituals-head">
          <h2 className="impact">How It Works</h2>
          <p>Simple setup, powerful results. Both products are designed to integrate seamlessly into your routine, whether you're hitting the gym or grinding games.</p>
        </div>
        <div className="rituals-grid">
          <div className="rituals-list">
            {ritualMoments.map((moment) => (
              <div key={moment} className="ritual-row">
                <span className="ritual-index" aria-hidden="true">
                  ●
                </span>
                <p>{moment}</p>
              </div>
            ))}
          </div>
          <div className="moment-card brutal-block">
            <div className="moment-top">
              <p className="eyebrow">For Streamers & VTubers</p>
              <h3 className="impact">Phase Pal: Your on-screen co-host.</h3>
            </div>
            <div className="moment-body">
              <p>
                Use Phase Pal as a reactive character during streams, customize voices to match your VTuber model, 
                or add a chaotic gremlin companion. Works offline, no cloud required.
              </p>
              <a href="https://youtu.be/GWu0iY_5KVA?si=bTASvfPjZjjvRZnv" target="_blank" rel="noopener noreferrer" className="cta ghost-cta small">
                Watch Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <StayConnected />
    </div>
  );
};

export default LandingPage;
